import React, { useState } from 'react';
import {
  Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails,
  Divider, Grid, Button, Tooltip, CircularProgress, Checkbox, Menu, MenuItem, Card,
} from '@mui/material';
import {
  ExpandMore, Speed, Memory, AutoFixHigh, OpenInNew, EnergySavingsLeaf, GppMaybe,
  NotInterested, GppGood, CheckCircle, BuildOutlined,
} from '@mui/icons-material';
import type { Issue } from '../../../store/api/githubApi';
import { SEVERITY_COLOR, isIssueFixable } from '../constants';
import LockedIssueRow from './LockedIssueRow';

function formatOwaspCategory(category: string): { short: string; full: string } {
  // "A03:2021-Injection" -> { short: "A03: Injection", full: "A03:2021 - Injection" }
  const [idPart, ...nameParts] = category.split('-');
  const name = nameParts.join('-');
  const id = idPart.split(':')[0];
  return { short: `${id}: ${name}`, full: `${idPart} - ${name}` };
}

export const FP_REASON_LABELS: Record<string, string> = {
  false_positive: 'False positive',
  already_mitigated: 'Already mitigated',
  wont_fix: "Won't fix",
  not_relevant: 'Not relevant',
};

// ── Tool Detail Panel ─────────────────────────────────────────────────────────

// Labeled by category, never by the underlying detection engine — the report is presented as
// KodeGauge's own analysis, not an assembly of third-party tools. tool_name still exists on the
// Issue model (useful internally — see the precision/recall test suites in debtiq-backend) but
// is never rendered in the UI.
export const CATEGORY_LABELS: Record<string, string> = {
  security:      'KodeGauge Security Analysis',
  complexity:    'KodeGauge Complexity Analysis',
  documentation: 'KodeGauge Documentation Analysis',
  dependencies:  'KodeGauge Dependency Audit',
  dead_code:     'KodeGauge Dead Code Detection',
};

export const ToolDetailPanel: React.FC<{ detail: string; category: string | null }> = ({ detail, category }) => {
  const isBanditFormat = detail.startsWith('What this means:');

  if (isBanditFormat) {
    const sections: Record<string, string> = {};
    detail.split(' | ').forEach((part) => {
      const colon = part.indexOf(':');
      if (colon !== -1) {
        const key = part.slice(0, colon).trim();
        sections[key] = part.slice(colon + 1).trim();
      }
    });
    const whatItMeans = sections['What this means'];
    const howToFix   = sections['How to fix'];
    const confidence = sections['Detection confidence'];
    const reference  = sections['Reference'];

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>WHAT IS THE PROBLEM</Typography>
          {confidence && (
            <Chip label={confidence} size="small" variant="outlined"
              sx={{ height: 16, fontSize: 9, fontWeight: 600 }} />
          )}
        </Box>
        {whatItMeans && (
          <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#fff8f0', borderRadius: 1.5, border: '1px solid #fed7aa' }}>
            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, overflowWrap: 'anywhere' }}>{whatItMeans}</Typography>
          </Box>
        )}
        {howToFix && (
          <>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>HOW TO FIX IT</Typography>
            <Box sx={{ mt: 0.5, mb: 1.5, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1.5, border: '1px solid #bbf7d0' }}>
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6, overflowWrap: 'anywhere' }}>{howToFix}</Typography>
            </Box>
          </>
        )}
        {reference && (
          <Typography variant="caption" color="text.disabled">
            <a href={reference} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
              {reference}
            </a>
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {category ? CATEGORY_LABELS[category] ?? 'KodeGauge Analysis' : 'KodeGauge Analysis'}
      </Typography>
      <Box sx={{ mt: 0.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, overflowWrap: 'anywhere' }}>{detail}</Typography>
      </Box>
    </Box>
  );
};

// ── Dismiss Button ────────────────────────────────────────────────────────────

const DismissButton: React.FC<{
  onDismiss: (reason: string) => void;
  disabled?: boolean;
}> = ({ onDismiss, disabled }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <>
      <Button
        size="small" variant="text" color="inherit"
        startIcon={<NotInterested fontSize="small" />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        Not relevant
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {Object.entries(FP_REASON_LABELS).map(([value, label]) => (
          <MenuItem key={value} onClick={() => { setAnchorEl(null); onDismiss(value); }}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// ── Issue Row ─────────────────────────────────────────────────────────────────

const IssueRow: React.FC<{
  issue: Issue; selected?: boolean;
  selectionDisabled?: boolean;
  onSelect?: (id: number, checked: boolean) => void;
  onFix?: (id: number) => void; isFixing?: boolean;
  onDismiss?: (id: number, reason: string) => void; isDismissing?: boolean;
  defaultExpanded?: boolean;
  /** Called instead of any normal interaction when issue.is_locked — see
   * LockedIssueRow below. Omitted entirely if the caller never passes it
   * (e.g. a context that can't show an upgrade modal), in which case the
   * locked row still renders but isn't clickable. */
  onLockedClick?: () => void;
}> = ({ issue, selected, selectionDisabled, onSelect, onFix, isFixing, onDismiss, isDismissing, defaultExpanded, onLockedClick }) => {
  if (issue.is_locked) {
    return <LockedIssueRow issue={issue} onClick={onLockedClick} />;
  }

  const hasPerf = issue.perf_estimated_compute_saving || issue.perf_estimated_memory_saving;
  const isLargeFileNoPerf = !hasPerf && issue.ai_fix_complexity === 'large_file';
  const hasCarbon = issue.carbon_co2g_per_month != null;
  const hasRisk = issue.security_risk_usd != null;
  const fixIsVerified = issue.fix_verified === true;
  const fixHasFailureReason = !!issue.fix_failure_reason;
  const isFixable = isIssueFixable(issue);
  const isGenerating = issue.status === 'fix_generating';
  const isPrRaised = issue.status === 'pr_raised';
  const isDismissed = issue.status === 'ignored';
  const canDismiss = !isPrRaised && !isGenerating && !isDismissed && !!onDismiss;

  return (
    <Accordion
      id={`issue-row-${issue.id}`}
      defaultExpanded={defaultExpanded}
      TransitionProps={{ unmountOnExit: true }}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid', borderColor: defaultExpanded ? 'primary.main' : isPrRaised ? 'success.200' : 'grey.200', mb: 1,
        boxShadow: defaultExpanded ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
        borderRadius: '8px !important', '&:before': { display: 'none' },
        overflow: 'hidden',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { borderColor: defaultExpanded ? 'primary.main' : 'grey.400' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: 'text.secondary' }} />}
        sx={{
          px: 2,
          py: 1,
          minHeight: 56,
          '&.Mui-expanded': { minHeight: 56 },
          '& .MuiAccordionSummary-content': {
            minWidth: 0,
            my: 0,
            mr: 1,
            '&.Mui-expanded': { my: 0 },
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', minWidth: 0 }}>
          {onSelect && isFixable && !isPrRaised && (
            <Checkbox size="small" checked={selected ?? false} disabled={isGenerating || selectionDisabled}
              onChange={(e) => { e.stopPropagation(); onSelect(issue.id, e.target.checked); }}
              onClick={(e) => e.stopPropagation()} sx={{ p: 0, mr: 0.25 }} />
          )}

          {/* Severity Badge */}
          <Chip label={issue.severity} size="small" color={SEVERITY_COLOR[issue.severity] ?? 'default'}
            sx={{ textTransform: 'capitalize', fontWeight: 700, minWidth: 68, height: 24, fontSize: '0.75rem', flexShrink: 0 }} />

          {/* Title & File Path Stack */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {issue.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {issue.file_path}{issue.line_start ? `:${issue.line_start}` : ''}
              </Typography>

              {issue.owasp_category && (
                <Tooltip title={`OWASP Top 10 (2021): ${formatOwaspCategory(issue.owasp_category).full}`}>
                  <Chip label={formatOwaspCategory(issue.owasp_category).short} size="small" variant="outlined"
                    sx={{ fontSize: 10, height: 18, flexShrink: 0, color: 'text.secondary', borderColor: 'grey.300', px: 0.5 }} />
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Standardised Right-Side Status Area */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 'auto' }}>
            {hasRisk && (
              <Tooltip title="Estimated breach exposure if left unfixed">
                <Chip icon={<GppMaybe sx={{ fontSize: '12px !important' }} />}
                  label={`$${issue.security_risk_usd!.toLocaleString()} risk`} size="small"
                  sx={{
                    height: 22, fontSize: 10, fontWeight: 600, flexShrink: 0,
                    bgcolor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
                    '& .MuiChip-icon': { color: '#dc2626' },
                  }} />
              </Tooltip>
            )}

            {isPrRaised && fixIsVerified && (
              <Tooltip title={issue.fix_verification_summary ?? 'Verified before this PR was raised'}>
                <Chip label="Verified" size="small" icon={<GppGood sx={{ fontSize: '12px !important' }} />}
                  sx={{
                    height: 22, fontSize: 10, fontWeight: 700, flexShrink: 0,
                    bgcolor: '#f0fdf4', color: '#15803d', border: '1px solid #86efac',
                    '& .MuiChip-icon': { color: '#16a34a' },
                  }} />
              </Tooltip>
            )}

            {/* Primary Remediation Status */}
            {isGenerating ? (
              <Chip icon={<CircularProgress size={12} color="inherit" />} label="Fixing…" color="info" size="small"
                sx={{ height: 24, fontWeight: 600, flexShrink: 0 }} />
            ) : isPrRaised ? (
              <Chip label={`✓ Fix Prepared · PR #${issue.pr_number}`} size="small" color="success"
                icon={<OpenInNew sx={{ fontSize: '12px !important' }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (issue.pr_url) window.open(issue.pr_url, '_blank');
                }}
                sx={{ height: 24, flexShrink: 0, cursor: 'pointer', fontWeight: 700, px: 0.5 }} />
            ) : isDismissed ? (
              <Tooltip title={issue.fp_reason ? FP_REASON_LABELS[issue.fp_reason] ?? issue.fp_reason : 'Dismissed'}>
                <Chip label="Dismissed" size="small" icon={<NotInterested sx={{ fontSize: '12px !important' }} />}
                  sx={{ height: 24, flexShrink: 0 }} />
              </Tooltip>
            ) : isFixable && onFix ? (
              <Button
                size="small"
                variant="contained"
                disableElevation
                color="primary"
                startIcon={<AutoFixHigh sx={{ fontSize: 13 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onFix(issue.id);
                }}
                disabled={isFixing}
                sx={{ textTransform: 'none', fontSize: 11, fontWeight: 800, py: 0.25, px: 1.25, flexShrink: 0 }}
              >
                AI Fix
              </Button>
            ) : isFixable ? null : (
              <Chip label="Manual Fix" size="small" variant="outlined"
                sx={{ height: 24, flexShrink: 0, fontSize: 11, color: 'text.secondary', borderColor: 'grey.300' }} />
            )}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Divider sx={{ mb: 2 }} />

        {/* Requirement 4: Fix Lifecycle Indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1.5, py: 0.75, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', flexWrap: 'wrap' }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">Lifecycle:</Typography>
          <Chip label="Finding" size="small" sx={{ height: 20, fontSize: 10, bgcolor: 'grey.200' }} />
          <Typography variant="caption" color="text.disabled">→</Typography>
          {isPrRaised ? (
            <>
              <Chip label="AI Fix Prepared ✓" size="small" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
              <Typography variant="caption" color="text.disabled">→</Typography>
              <Chip label={`PR #${issue.pr_number} Created ✓`} size="small" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
              <Typography variant="caption" color="text.disabled">→</Typography>
              <Chip label="Review PR" size="small" variant="outlined" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
            </>
          ) : isFixable ? (
            <>
              <Chip label="✨ AI Fix Available" size="small" color="primary" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
              <Typography variant="caption" color="text.disabled">→</Typography>
              <Chip label="Ready to Fix" size="small" variant="outlined" color="primary" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
            </>
          ) : (
            <Chip label="Manual Fix Required" size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
          )}
        </Box>

        {issue.tool_detail && <ToolDetailPanel detail={issue.tool_detail} category={issue.category} />}

        {issue.ai_explanation ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>WHY THIS MATTERS</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{issue.ai_explanation}</Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300',
            borderRadius: 1.5, px: 1.5, py: 1 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              AI analysis pending — resolve higher-priority issues and re-scan to unlock insights for this issue.
            </Typography>
          </Box>
        )}

        {/* Requirement 2 & 3: Primary Action Callout Box */}
        <Card elevation={0} sx={{
          p: 2, mb: 2, borderRadius: 2,
          border: '1px solid', borderColor: isPrRaised ? 'success.300' : isFixable ? 'primary.300' : 'grey.300',
          bgcolor: isPrRaised ? '#f0fdf4' : isFixable ? '#f0f9ff' : 'grey.50',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
        }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isPrRaised ? (
                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
              ) : isFixable ? (
                <AutoFixHigh sx={{ color: 'primary.main', fontSize: 20 }} />
              ) : (
                <BuildOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
              )}
              <Typography variant="subtitle2" fontWeight={800} color={isPrRaised ? 'success.900' : isFixable ? 'primary.900' : 'text.primary'}>
                {isPrRaised ? `✓ Fix prepared · Pull Request #${issue.pr_number}` : isFixable ? '✨ AI-assisted fix available' : 'Manual remediation required'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.4 }}>
              {isPrRaised
                ? `Pull Request #${issue.pr_number} has been created and verified for this finding.`
                : isFixable
                ? `Estimated fix time: ${issue.ai_estimated_fix_time || '30 minutes'} · KodeGauge can generate a pull request automatically.`
                : 'This issue requires manual code changes by an engineer.'}
            </Typography>
          </Box>
          <Box>
            {isPrRaised && issue.pr_url ? (
              <Button
                variant="contained" color="success" disableElevation size="small"
                startIcon={<OpenInNew />} onClick={() => window.open(issue.pr_url!, '_blank')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                View Pull Request →
              </Button>
            ) : isGenerating ? (
              <Button variant="contained" disabled size="small" startIcon={<CircularProgress size={14} color="inherit" />} sx={{ textTransform: 'none' }}>
                Fix in progress…
              </Button>
            ) : isFixable && onFix ? (
              <Button
                variant="contained" color="primary" disableElevation size="small"
                startIcon={isFixing ? <CircularProgress size={14} color="inherit" /> : <AutoFixHigh />}
                onClick={() => onFix(issue.id)} disabled={isFixing}
                sx={{ textTransform: 'none', fontWeight: 700, px: 2, py: 0.75 }}
              >
                Fix this issue →
              </Button>
            ) : null}
          </Box>
        </Card>

        {/* Requirement 6: Finding Summary Grid with 5th "Remediation" Column */}
        <Grid container spacing={2} sx={{ mb: (hasPerf || isLargeFileNoPerf) ? 2 : 0 }}>
          {issue.ai_estimated_fix_time && (
            <Grid item xs={6} sm={2.4}>
              <Typography variant="caption" color="text.secondary" display="block">Fix time</Typography>
              <Typography variant="body2" fontWeight={500}>{issue.ai_estimated_fix_time}</Typography>
            </Grid>
          )}
          {issue.ai_priority_score !== null && (
            <Grid item xs={6} sm={2.4}>
              <Typography variant="caption" color="text.secondary" display="block">Recommended priority</Typography>
              <Typography variant="body2" fontWeight={500}>{issue.ai_priority_score?.toFixed(0)}/100</Typography>
            </Grid>
          )}
          {issue.ai_fix_complexity && (
            <Grid item xs={6} sm={2.4}>
              <Typography variant="caption" color="text.secondary" display="block">Fix complexity</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>{issue.ai_fix_complexity}</Typography>
            </Grid>
          )}
          <Grid item xs={6} sm={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">Remediation</Typography>
            <Typography variant="body2" fontWeight={700} color={isPrRaised ? 'success.main' : isFixable ? 'primary.main' : 'text.secondary'}>
              {isPrRaised ? `Already Fixed (PR #${issue.pr_number})` : isFixable ? '✨ AI Fix Available' : 'Manual'}
            </Typography>
          </Grid>
        </Grid>

        {hasPerf && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Speed fontSize="small" color="info" />
              <Typography variant="caption" fontWeight={700} color="info.dark">PERFORMANCE SAVINGS ESTIMATE</Typography>
              {issue.perf_confidence && (
                <Chip label={`${issue.perf_confidence} confidence`} size="small" color="info" variant="outlined" sx={{ ml: 'auto' }} />
              )}
            </Box>
            <Grid container spacing={2}>
              {issue.perf_estimated_compute_saving && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Speed fontSize="small" sx={{ color: 'info.main', fontSize: 16 }} />
                    <Typography variant="caption" color="text.secondary">Compute</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{issue.perf_estimated_compute_saving}</Typography>
                </Grid>
              )}
              {issue.perf_estimated_memory_saving && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Memory fontSize="small" sx={{ color: 'info.main', fontSize: 16 }} />
                    <Typography variant="caption" color="text.secondary">Memory</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{issue.perf_estimated_memory_saving}</Typography>
                </Grid>
              )}
            </Grid>
            {issue.perf_notes && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, overflowWrap: 'anywhere' }}>
                {issue.perf_notes}
              </Typography>
            )}
          </Box>
        )}

        {isLargeFileNoPerf && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed', borderColor: 'grey.300' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Speed fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary">PERFORMANCE ANALYSIS NOT RUN</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {issue.file_loc
                ? `This file (${issue.file_loc.toLocaleString()} lines) exceeds the 500-line threshold for per-issue performance analysis`
                : 'This file exceeds the size threshold for per-issue performance analysis'}
              {' — issues in it are assessed together as a file-level health summary instead.'}
            </Typography>
          </Box>
        )}

        {hasCarbon && (
          <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid', borderColor: '#86efac' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <EnergySavingsLeaf fontSize="small" sx={{ color: '#16a34a' }} />
              <Typography variant="caption" fontWeight={700} color="#15803d">CARBON IMPACT ESTIMATE</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Energy saving</Typography>
                <Typography variant="body2" fontWeight={600} color="#15803d">
                  {issue.carbon_kwh_per_month?.toFixed(2)} kWh/month
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">CO₂ equivalent</Typography>
                <Typography variant="body2" fontWeight={600} color="#15803d">
                  {issue.carbon_co2g_per_month! >= 1000
                    ? `${(issue.carbon_co2g_per_month! / 1000).toFixed(2)} kg CO₂/month`
                    : `${Math.round(issue.carbon_co2g_per_month!)} g CO₂/month`}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {fixHasFailureReason && (
          <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fef2f2', border: '1px solid', borderColor: '#fecaca', borderRadius: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="error.dark" display="block" sx={{ mb: 0.5 }}>
              Why the automated fix didn&apos;t merge
            </Typography>
            <Typography variant="caption" color="error.dark" display="block" sx={{ overflowWrap: 'anywhere', lineHeight: 1.6 }}>
              {issue.fix_failure_reason}
            </Typography>
          </Box>
        )}

        {/* Requirement 5: Improved Finding Footer */}
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isPrRaised && issue.pr_url ? (
              <Button size="small" variant="contained" color="success" disableElevation
                startIcon={<OpenInNew fontSize="small" />} onClick={() => window.open(issue.pr_url!, '_blank')}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                View Pull Request #{issue.pr_number} →
              </Button>
            ) : isGenerating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Fix in progress…</Typography>
              </Box>
            ) : isFixable && onFix ? (
              <Button size="small" variant="contained" disableElevation
                startIcon={isFixing ? <CircularProgress size={14} color="inherit" /> : <AutoFixHigh fontSize="small" />}
                onClick={() => onFix(issue.id)} disabled={isFixing}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                ✨ Fix this issue →
              </Button>
            ) : !isFixable ? (
              <Typography variant="caption" color="text.disabled" fontWeight={600}>
                Manual remediation required
              </Typography>
            ) : null}
          </Box>

          <Box>
            {canDismiss && (
              <DismissButton
                onDismiss={(reason) => onDismiss!(issue.id, reason)}
                disabled={isDismissing}
              />
            )}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(IssueRow);
